import React, { HTMLAttributes } from "react";
import { UserThumbnail } from "@kaltura/mediaspace-shared-user";
import { Typography } from "@kaltura/ds-react-components";
import { EmailAutocompleteField } from "../types";
import { Container, StyledFormLabel } from "@kaltura/mediaspace-shared-ui/controlled-form";
import { Controller, useFormContext } from "react-hook-form";
import { emailRegExString, isEmailRestricted, translate } from "@kaltura/mediaspace-shared-utils";
import { FormFieldError, LiveAutoComplete } from "@kaltura/mediaspace-shared-ui/form";
import { styled } from "@mui/material";
import { flexGap, LabelContainer } from "./styled-components";

export interface EmailAutocompleteProps {
    field: EmailAutocompleteField;
}

export const EmailAutocomplete = ({ field }: EmailAutocompleteProps) => {
    const { label, id, required, disabled, autocompleteUrl, onUserSelected, restrictedDomains = [] } = field;
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const error = errors[id];
    const errorMessage = String(error?.message || "");

    const getAutoCompleteOption = (
        props: HTMLAttributes<HTMLLIElement>,
        option: any,
        { selected }: { selected: boolean }
    ) => {
        const user = option.value;
        const fullName = `${user.firstName} ${user.lastName}`;
        return (
            <StyledLi {...props} key={user.id}>
                <UserThumbnail name={fullName} image={user.thumbnailUrl} size={24} initialsSize={"small"} />
                <LabelContainer>
                    <Typography variant={"body2Highlight"}>{fullName}</Typography>
                    <Typography variant={"body2"} component={"span"}>
                        {user.email}
                    </Typography>
                </LabelContainer>
            </StyledLi>
        );
    };

    return (
        <Container>
            <StyledFormLabel htmlFor={id} required={required} text={label} fontWeightBold={true} />
            <div>
                <Controller
                    control={control}
                    name={id}
                    defaultValue={[]}
                    rules={{
                        validate: (data) => {
                            const email = data?.value?.email?.trim() || data?.value?.trim() || "";

                            if (required && !email) {
                                return translate("Field %1 is required", [label]);
                            }

                            if (!emailRegExString.test(email)) {
                                return translate("Please use a valid email address");
                            }

                            //If restrictedDomains are provided, validate against them only from free text input
                            const isFreeTyped = typeof data?.value === "string";
                            if (isFreeTyped && restrictedDomains && restrictedDomains.length > 0) {
                                if (isEmailRestricted(email, restrictedDomains)) {
                                    return translate("Cannot invite new users from this email domain");
                                }
                            }

                            return true;
                        },
                    }}
                    render={({ field: { ref, value, onChange, ...props } }) => (
                        <LiveAutoComplete
                            id={id}
                            url={autocompleteUrl}
                            freeSolo={true}
                            multiple={false}
                            selectOnFocus={true}
                            clearOnBlur={true}
                            emitChangeEventOnNewValues={true}
                            options={[]}
                            inputRef={ref}
                            hasError={errorMessage.length > 0}
                            value={value || []}
                            minInputLength={3}
                            disabled={disabled}
                            onChange={(event, data) => {
                                onChange(data);
                                if (onUserSelected) {
                                    const user = data.value;
                                    onUserSelected({
                                        email: user.email,
                                        firstName: user.firstName,
                                        lastName: user.lastName,
                                        id: user.id,
                                    });
                                }
                            }}
                            renderOption={getAutoCompleteOption}
                            {...props}
                        />
                    )}
                />
                <div aria-live="polite">{errorMessage && <FormFieldError>{errorMessage}</FormFieldError>}</div>
            </div>
        </Container>
    );
};

const StyledLi = styled("li")(({ theme }) => ({
    ...flexGap(theme),
}));
